import React, { type JSX, useCallback, useContext, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import type { Store } from "stores/store";
import { inject, observer } from "mobx-react";
import { UiState } from "@/components/enums/UiState";
import { drawImageToCanvas, uploadImage } from "@/components/shared/webcam/utils";
import useCaptureFace from "./hooks/useCaptureFace";
import useVideoConstraints from "./hooks/useVideoConstraints";
import useUserDevice from "./hooks/useUserDevice";
import useHandleResize from "./hooks/useHandleResize";
import WebcamCaptureMask from "./WebcamCaptureMask";
import { cn } from "@/lib/utils";
import { ImageProcessingContext } from "../imageProcessing/ImageProcessing";
import log from "../../../../utils/logger";

type WebcamCaptureProps = {
    store?: Store;
    className?: string;
    showBlur?: boolean;
    showMask?: boolean;
    showToggleIfAvailable?: boolean;
};

const WebcamCapture = ({
    store,
    className,
    showBlur = false,
    showMask = false,
    showToggleIfAvailable = false,
}: WebcamCaptureProps): JSX.Element => {
    const webcamRef = useRef<Webcam>(null);
    const currentlyInProcess = useRef<boolean>(false);

    const { submitImage } = useContext(ImageProcessingContext);

    const isOnline: boolean = process.env.NEXT_PUBLIC_IS_ONLINE === "true";
    const processImageAPI: string =
        process.env.NEXT_PUBLIC_LOCAL_IMAGE_PROCESSING_URL ?? "http://127.0.0.1:5000/process-image";

    if (!store) {
        throw Error("Component was not setup correctly");
    }

    const {
        updateImageAgedUrl,
        captureFace,
        updateCaptureFace,
        updateRenderContent,
        updateIsCameraAllowed,
        updateImagePresentUrl,
        isCameraAllowed,
        updateAvatarImage,
        updateImageProcessingState,
    } = store;
    const [mediaError, setMediaError] = useState<boolean>(false);
    const { videoConstraints, toggleCameraDirection } = useVideoConstraints(isOnline, mediaError);

    console.log("videoConstraints", videoConstraints);

    console.log(videoConstraints);
    const { device } = useUserDevice();
    const {
        windowSize: {
            current: { width, height },
        },
    } = useHandleResize();
    const camSize: number = height > width ? height : width;

    const showChat = () => {
        //updateRenderContent(UiState.CHAT);
        updateCaptureFace(false);
    };

    const getScreenshotWithRetry = async (): Promise<string | null | undefined> => {
        for (let attempt = 0; attempt < 5; attempt++) {
            const screenshot = webcamRef.current?.getScreenshot();
            if (screenshot) return screenshot;
            await new Promise((resolve) => setTimeout(resolve, 200));
        }

        return webcamRef.current?.getScreenshot();
    };

    const capture = useCallback(async () => {
        if (currentlyInProcess.current) return;
        currentlyInProcess.current = true;

        updateRenderContent(UiState.LOADING);

        const imageSrc = await getScreenshotWithRetry();


        if (imageSrc) {
            // updateImagePresentUrl(imageSrc)
            const img = new Image();
            img.src = imageSrc;
            await img.decode();

            updateAvatarImage(imageSrc)

            let canvas: HTMLCanvasElement | null = null;

            try {
                canvas = await drawImageToCanvas(img, isOnline);
            } catch (e) {
                if (e instanceof Error) {
                    log.error(e.message);
                } else {
                    log.error("An unknown error occurred.");
                }
            }

            if (canvas !== null) {
                canvas.toBlob(async (blob: Blob | null) => {
                    if (!blob) {
                        updateImageProcessingState({
                            ...store.imageProcessingState,
                            processingAborted: true,
                        });
                        currentlyInProcess.current = false;
                        return;
                    }

                    try {
                        if (isOnline) {
                            await submitImage(blob);
                        } else {
                            const { agedUrl, cutoutUrl } = await uploadImage(blob, processImageAPI);

                            updateImageAgedUrl(agedUrl);
                            updateImagePresentUrl(cutoutUrl);

                            showChat();
                        }
                    } catch (e) {
                        if (e instanceof Error) {
                            log.error("Error processing image:", e.message);
                        } else {
                            log.error("An unknown error occurred.");
                        }
                        updateImageAgedUrl(process.env.NEXT_PUBLIC_BASE_PATH + "images/test/test_tim.png");
                        showChat();
                    } finally {
                        currentlyInProcess.current = false;
                    }
                });
            } else {
                updateImageProcessingState({
                    ...store.imageProcessingState,
                    processingAborted: true,
                });
                currentlyInProcess.current = false;
            }
        } else {
            console.error("WebcamCapture: could not capture a screenshot from the webcam.");
            updateImageProcessingState({
                ...store.imageProcessingState,
                processingAborted: true,
            });
            currentlyInProcess.current = false;
        }
    }, [webcamRef, isOnline, updateCaptureFace, updateImageAgedUrl, updateRenderContent]);

    useCaptureFace(captureFace, capture); // state change on captureFace is triggering capture

    return (
        <div className={`scrollbar-hidden absolute h-full w-full overflow-hidden ${className}`}>
            {/* {
                isOnline && device?.isHandheld ?
                    <div className="absolute left-0 z-10 p-2 flex w-full justify-between font-frutigerBlackItalic">
                        <button className="bg-white text-black rounded-full px-6 pt-3 pb-2 text-lg pointer-events-auto"
                            onClick={toggleCameraDirection} type="button">
                            Toggle Cam
                        </button>
                    </div> : null
            } */}

            {/*width and height for online need to be a fixed bounding box of 1536x1536*/}
            <div
                className={`${isOnline ? `absolute h-[1536px] w-[1536px]` : "absolute -left-[840px] h-[3840px] w-[3840px]"}`}
            >
                {videoConstraints.frameRate > 0 && (
                    <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/png"
                        className={`h-full w-full ${isOnline ? "-scale-x-100" : "-rotate-90"}`}
                        screenshotQuality={1}
                        videoConstraints={videoConstraints}
                    />
                )}
            </div>

            <div className="h-full w-full">
                <div className={cn("w-100 absolute bottom-0")} style={{ height: "100%", width: "100%" }}>
                    {videoConstraints.frameRate > 0 && (
                        <Webcam
                            audio={false}
                            videoConstraints={videoConstraints}
                            className={`h-full w-full object-cover ${isOnline ? "-scale-x-100" : "-rotate-90"}`}
                            onUserMedia={() => {
                                console.log("No error occured, camera is allowed");
                                updateIsCameraAllowed(true);
                            }}
                            onUserMediaError={(e) => {
                                console.error("onUserMediaError", e);
                                updateIsCameraAllowed(false);
                                if ((e as Error)?.name === "NotAllowedError") {
                                    updateIsCameraAllowed(false);
                                    console.error("Permission denied: NotAllowedError");
                                    // TODO: activate the error hint screen and do not proceed
                                } else {
                                    console.error("An error occurred:", e);
                                }
                                setMediaError(true);
                            }}
                        />
                    )}
                </div>
                {showBlur && (
                    <div
                        className={"absolute h-full w-full backdrop-blur-lg"}
                        style={{
                            background: "radial-gradient(circle, rgba(41,41,41,0.35) 30%, rgba(41,41,41,0.72) 100%)",
                        }}
                    />
                )}
                {showMask && <WebcamCaptureMask className={"absolute h-full w-full"} />}
            </div>
        </div>
    );
};

export default inject("store")(observer(WebcamCapture));
